import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import type { PartExchange as PartExchangeType } from '@havenengineering/module-owners-content/dist/types/part-exchange';
import Accordion from '@havenengineering/module-shared-library/dist/components/Accordion';
import {
  InlineMessaging,
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';

import ContentfulPicture from '../../components/ContentfulPicture/ContentfulPicture';
import GiveMeACallButton from '../../components/PartExchange/GiveMeACallButton';
import { fetchWrapper, withApiBaseUrl, withBFFUrl } from '../../helpers/fetch';
import { pageView } from '../../helpers/googleTag';
import { useRuntimeConfig } from '../../hooks/useRuntimeConfig';
import styles from '../../styles/pages/partExchange.module.scss';
import { OwnerDetails } from '../../types/part-exchange';

const PartExchange = () => {
  const { PUBLIC_ENV } = useRuntimeConfig();
  const { gtmPageViewAuthInfo, activeAccountId, loggedInUser, activeAccount } =
    useAuthContext();
  const { isFallback } = useRouter();
  const [enquiryDetails, setEnquiryDetails] = useState<OwnerDetails>();
  const [enquiryDetailsError, setEnquiryDetailsError] = useState<any>();

  const [pageData, setPageData] = useState<PartExchangeType | null>(null);
  const [errorHappened, setErrorHappened] = useState(false);

  const fetchVanInformation = async () => {
    try {
      const van = await fetchWrapper(
        withApiBaseUrl(`/account/van?accountId=${activeAccountId}`),
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      return van;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const getPageData = async () => {
      const parkCode = activeAccount?.parkCode;

      if (!parkCode) {
        return;
      }

      try {
        const pageData = await fetchWrapper(
          withBFFUrl(
            `/owners-content/part-exchange/${parkCode.toLocaleLowerCase()}`
          ),
          {
            method: 'GET',
          }
        );

        setPageData(pageData);
        setErrorHappened(false);
      } catch (error) {
        setErrorHappened(true);
        setPageData(null);
      }
    };

    getPageData();
  }, [activeAccount?.parkCode]);

  useEffect(() => {
    const getOwnerDetails = async () => {
      try {
        if (loggedInUser) {
          const van = await fetchVanInformation();

          pageView(
            'dashboard',
            `/ga-virtual/owners-account${window.location.pathname.toLowerCase()}`,
            'owners account',
            van
              ? {
                  ...gtmPageViewAuthInfo,
                  vanModel: van.model,
                  vanId: activeAccount?.vanID,
                }
              : gtmPageViewAuthInfo
          );

          const name =
            PUBLIC_ENV === 'production' ? loggedInUser.name : 'Mr Test User';
          const [title, firstName, ...lastName] = name.split(' ');

          const ownerDetails: OwnerDetails = {
            interestedIn: 'existing owner part exchange enquiry',
            parkInterestedIn: gtmPageViewAuthInfo?.parkCode || '',
            generalMessage: `I am an existing owner interested in speaking to someone about my part exchange. My account number is ${activeAccountId}.`,
            title: title,
            firstName: firstName,
            lastName: lastName.join(' '),
            email: loggedInUser.email?.toLowerCase() || '',
            tel:
              loggedInUser.dayTel ||
              loggedInUser.eveTel ||
              loggedInUser.mobTel ||
              '',
            postcode: loggedInUser.postCode || '',
            country: `UK`,
            addressLine1: loggedInUser.address1 || '',
            addressLine2: loggedInUser.address2 || '',
            town: loggedInUser.address3 || '',
            emailOptIn: false,
            timeToCall: 'NA',
            telesalesSubject: `Arrange a callback with owner ${activeAccountId} about their part exchange.`,
            referer:
              typeof window !== 'undefined'
                ? `${location.protocol}//${location.host}${location.pathname}`
                : '',
            ipAddress: '0.0.0.0',
            sourceCode: 'F',
            sourceSubCode: 'PLHAVEN',
          };
          setEnquiryDetails(ownerDetails);
        }
      } catch (error) {
        setEnquiryDetailsError({
          message: "Couldn't fetch owner details.",
          error,
        });
      }
    };

    getOwnerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (errorHappened) {
    return (
      <div>
        <InlineMessaging
          type={InlineMessagingTheme.ERROR_THEME}
          title="Error"
          message={'Something went wrong, please try again.'}
          dismissible={false}
        />
      </div>
    );
  }

  if (isFallback || !pageData) return null;

  return (
    <div className={styles.root}>
      <ContentfulPicture
        url={pageData?.hero}
        alt="Part exchange banner"
        profiles={[
          { width: 1296, height: 536, rule: '800px' },
          { width: 966, height: 400, rule: '500px' },
          { width: 653, height: 270, rule: '0' },
        ]}
      />

      <div className={styles.section}>
        <h1>Part exchange</h1>

        <div
          className={styles.content}
          dangerouslySetInnerHTML={{ __html: pageData?.introText }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.content}>
          <div dangerouslySetInnerHTML={{ __html: pageData?.bodyText }} />
          {!enquiryDetailsError && enquiryDetails && (
            <div className={styles.bodyCtaContainer}>
              <GiveMeACallButton
                enquiryDetails={enquiryDetails}
                subject={'part exchange'}
                buttonText={pageData?.bodyButtonText}
              />
            </div>
          )}
        </div>
      </div>
      {pageData?.termsContent && (
        <Accordion title={'Terms and conditions'}>
          <div dangerouslySetInnerHTML={{ __html: pageData?.termsContent }} />
        </Accordion>
      )}
    </div>
  );
};

export const getServerSideProps = () => ({
  props: {
    pageTitle: 'Part Exchange',
  },
});

export default PartExchange;
